import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { DollarSign, RefreshCw, Save, AlertTriangle, CloudDownload, Eye, EyeOff } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminBalance = () => {
  const [currentBalance, setCurrentBalance] = useState(null);
  const [newBalance, setNewBalance] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [syncError, setSyncError] = useState(null);
  const [isManual, setIsManual] = useState(false);
  const [showBalancePublic, setShowBalancePublic] = useState(true);
  const [settingsLoading, setSettingsLoading] = useState(false);

  const fetchCurrentBalance = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${BACKEND_URL}/api/balance`);
      setCurrentBalance(response.data.balance);
      setLastUpdated(response.data.last_updated);
      setNewBalance(response.data.balance?.toString() || "");
      setIsManual(response.data.is_manual || false);
    } catch (err) {
      toast.error("Error fetching balance");
    } finally {
      setLoading(false);
    }
  };

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/settings`);
      setShowBalancePublic(response.data.show_bank_balance);
    } catch (err) {
      console.error("Error fetching settings");
    }
  };

  useEffect(() => {
    fetchCurrentBalance();
    fetchSettings();
  }, []);

  const handleSyncMonarch = async () => {
    try {
      setSyncing(true);
      setSyncError(null);
      const response = await axios.get(`${BACKEND_URL}/api/balance?force_refresh=true`);
      
      if (response.data.error) {
        setSyncError(response.data.error);
        toast.error("Sync failed - see error below");
      } else {
        setCurrentBalance(response.data.balance);
        setLastUpdated(response.data.last_updated);
        setNewBalance(response.data.balance?.toString() || "");
        setIsManual(response.data.is_manual || false);
        toast.success("Synced with Monarch successfully!");
      }
    } catch (err) {
      setSyncError("Failed to connect to Monarch API");
      toast.error("Sync failed");
    } finally {
      setSyncing(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!newBalance || parseFloat(newBalance) < 0) {
      toast.error("Please enter a valid balance");
      return;
    }

    try {
      setSaving(true);
      await axios.post(`${BACKEND_URL}/api/balance/manual`, {
        balance: parseFloat(newBalance)
      });
      toast.success("Balance updated successfully");
      fetchCurrentBalance();
    } catch (err) {
      toast.error("Error updating balance");
    } finally {
      setSaving(false);
    }
  };

  const toggleBalanceVisibility = async () => {
    try {
      setSettingsLoading(true);
      const newValue = !showBalancePublic;
      await axios.patch(`${BACKEND_URL}/api/settings`, {
        show_bank_balance: newValue,
        show_upcoming_costs: true // Keep other setting unchanged - we'll fetch it properly
      });
      // Fetch full settings to ensure we have correct values
      const response = await axios.get(`${BACKEND_URL}/api/settings`);
      setShowBalancePublic(response.data.show_bank_balance);
      toast.success(newValue ? "Bank balance section is now visible" : "Bank balance section is now hidden");
    } catch (err) {
      toast.error("Error updating visibility");
    } finally {
      setSettingsLoading(false);
    }
  };

  const formatDate = (isoString) => {
    if (!isoString) return "Never";
    const date = new Date(isoString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] py-12" data-testid="admin-balance-page">
      <div className="max-w-xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <span className="font-mono text-[#FFD700] text-sm uppercase tracking-widest mb-4 block">
            Admin Panel
          </span>
          <h1 className="font-['Bebas_Neue'] text-5xl text-white mb-4">
            Update Bank Balance
          </h1>
          <p className="text-[#A3A3A3]">
            Manually update the bank account balance displayed on the Donate page.
          </p>
        </motion.div>

        {/* Visibility Toggle */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.05 }}
          className="card-tactical p-6 mb-8"
        >
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-white font-semibold mb-1">Show on Public Website</h3>
              <p className="text-[#666] text-xs">
                {showBalancePublic 
                  ? "Bank balance section is visible on the Donate page" 
                  : "Bank balance section is hidden from the Donate page"}
              </p>
            </div>
            <button
              onClick={toggleBalanceVisibility}
              disabled={settingsLoading}
              className={`relative w-14 h-8 rounded-full transition-colors ${
                showBalancePublic ? 'bg-green-600' : 'bg-[#333]'
              }`}
            >
              <span
                className={`absolute top-1 w-6 h-6 bg-white rounded-full transition-transform flex items-center justify-center ${
                  showBalancePublic ? 'left-7' : 'left-1'
                }`}
              >
                {showBalancePublic ? (
                  <Eye className="w-3 h-3 text-green-600" />
                ) : (
                  <EyeOff className="w-3 h-3 text-[#666]" />
                )}
              </span>
            </button>
          </div>
        </motion.div>

        {/* Balance Update Form */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="card-tactical p-8"
        >
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <RefreshCw className="w-8 h-8 text-[#FFD700] animate-spin" />
            </div>
          ) : (
            <>
              {/* Current Balance Display */}
              <div className="mb-6 p-4 bg-[#0A0A0A] border border-[#4A5320]">
                <div className="flex justify-between items-start mb-2">
                  <p className="text-[#A3A3A3] text-xs font-mono uppercase tracking-widest">
                    Current Displayed Balance
                  </p>
                  <span className={`text-xs font-mono uppercase tracking-widest px-2 py-0.5 ${
                    isManual 
                      ? 'bg-yellow-900/50 text-yellow-500 border border-yellow-700' 
                      : 'bg-green-900/50 text-green-400 border border-green-700'
                  }`}>
                    {isManual ? 'Manual' : 'Synced'}
                  </span>
                </div>
                <p className="font-['Bebas_Neue'] text-4xl text-[#FFD700]">
                  ${currentBalance?.toFixed(2) || '0.00'}
                </p>
                <p className="text-[#666] text-xs mt-2">
                  Last updated: {formatDate(lastUpdated)}
                </p>
                {isManual && (
                  <p className="text-yellow-600 text-xs mt-1">
                    Auto-sync paused. Click "Sync with Monarch" to resume hourly updates.
                  </p>
                )}
                {!isManual && (
                  <p className="text-green-600 text-xs mt-1">
                    Auto-syncing with Monarch every hour.
                  </p>
                )}
              </div>

              {/* Sync with Monarch Button */}
              <div className="mb-8">
                <button
                  onClick={handleSyncMonarch}
                  disabled={syncing}
                  className="w-full flex items-center justify-center gap-2 py-3 border border-[#4A5320] text-[#A3A3A3] hover:text-white hover:border-[#FFD700] transition-colors disabled:opacity-50"
                >
                  {syncing ? (
                    <>
                      <RefreshCw className="w-5 h-5 animate-spin" />
                      Syncing with Monarch...
                    </>
                  ) : (
                    <>
                      <CloudDownload className="w-5 h-5" />
                      Sync with Monarch Money
                    </>
                  )}
                </button>
                
                {syncError && (
                  <div className="mt-3 p-3 bg-red-900/20 border border-red-700">
                    <p className="text-red-400 text-xs font-mono">
                      {syncError}
                    </p>
                    <p className="text-[#666] text-xs mt-1">
                      Use manual update below if Monarch is unavailable.
                    </p>
                  </div>
                )}
              </div>

              <div className="border-t border-[#4A5320] pt-6 mb-6">
                <p className="text-[#A3A3A3] text-xs font-mono uppercase tracking-widest mb-4 text-center">
                  — Or Update Manually —
                </p>
              </div>

              {/* Update Form */}
              <form onSubmit={handleSave}>
                <label className="block text-[#A3A3A3] text-sm font-mono uppercase tracking-widest mb-2">
                  New Balance
                </label>
                <div className="relative mb-4">
                  <DollarSign className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-[#666]" />
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={newBalance}
                    onChange={(e) => setNewBalance(e.target.value)}
                    placeholder="Enter new balance"
                    className="w-full pl-12 pr-4 py-4 bg-[#0A0A0A] border border-[#4A5320] text-white text-xl focus:outline-none focus:border-[#FFD700]"
                  />
                </div>

                <div className="flex items-start gap-2 mb-6 p-3 bg-yellow-900/20 border border-yellow-700">
                  <AlertTriangle className="w-4 h-4 text-yellow-500 flex-shrink-0 mt-0.5" />
                  <p className="text-yellow-500 text-xs">
                    This will immediately update the balance shown on the public Donate page.
                  </p>
                </div>

                <button
                  type="submit"
                  disabled={saving}
                  className="btn-primary w-full flex items-center justify-center gap-2"
                >
                  {saving ? (
                    <>
                      <RefreshCw className="w-5 h-5 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="w-5 h-5" />
                      Update Balance
                    </>
                  )}
                </button>
              </form>
            </>
          )}
        </motion.div>

        {/* Navigation Links */}
        <div className="text-center mt-8 space-y-2">
          <a href="/admin/costs" className="block text-[#FFD700] font-mono text-sm uppercase tracking-widest hover:underline">
            Manage Upcoming Costs →
          </a>
          <a href="/donate" className="block text-[#666] font-mono text-sm uppercase tracking-widest hover:text-white transition-colors">
            ← Back to Donate Page
          </a>
        </div>
      </div>
    </div>
  );
};

export default AdminBalance;
